import React, { useState, useEffect } from "react"
import {
  Card,
  Select,
  Input,
  Statistic,
  Row,
  Col,
  Table,
  Spin,
  DatePicker,
} from "antd"
import {
  DollarOutlined,
  UserOutlined,
  ApiOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"
import { AxiosResponse } from "axios"
import {
  AiUsageMetricsResponse,
  GeneralMetricsResponse,
  TopUsersResponse,
  TopUnitsResponse,
} from "api/Superadmin/superadmin"
import { AiUsageMetricsQueryParams } from "features/Superadmin/api/useSuperadmin"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Dayjs } from "dayjs"

const { Option } = Select

interface AiMetricsDisplayProps {
  getAiUsageMetrics: (
    queryParams?: AiUsageMetricsQueryParams
  ) => Promise<AxiosResponse<AiUsageMetricsResponse>>
  isLoadingAiMetrics: boolean
  units: string[]
}

/**
 * AiMetricsDisplay component provides an interface for viewing AI usage metrics.
 * It allows users to filter by period, user, unit, and view top users/units.
 */
const AiMetricsDisplay: React.FC<AiMetricsDisplayProps> = ({
  getAiUsageMetrics,
  isLoadingAiMetrics,
  units,
}) => {
  const [metricsData, setMetricsData] = useState<AiUsageMetricsResponse | null>(
    null
  )
  const [viewType, setViewType] = useState<
    "general" | "top-users" | "top-units"
  >("general")
  const [period, setPeriod] = useState<string>("today")
  const [selectedUser, setSelectedUser] = useState<string | undefined>(
    undefined
  )
  const [selectedUnit, setSelectedUnit] = useState<string | undefined>(
    undefined
  )
  const [customDate, setCustomDate] = useState<Dayjs | null>(null)
  const [limit, setLimit] = useState<number>(10)

  /**
   * Fetches AI usage metrics based on current filter selections
   */
  const fetchMetrics = async (): Promise<void> => {
    try {
      const queryParams: AiUsageMetricsQueryParams = {}

      if (viewType === "general") {
        // For general metrics
        if (period === "custom" && customDate) {
          queryParams.period = customDate.format("YYYY-MM-DD")
        } else if (period !== "startup") {
          queryParams.period = period
        }

        if (selectedUser) queryParams.user = selectedUser
        if (selectedUnit) queryParams.unit = selectedUnit
      } else {
        // For top users/units
        queryParams.top = viewType === "top-users" ? "users" : "units"
        if (period !== "startup") {
          queryParams.period =
            period === "custom" && customDate
              ? customDate.format("YYYY-MM-DD")
              : period
        }
        queryParams.limit = limit
      }

      const response = await getAiUsageMetrics(queryParams)
      setMetricsData(response.data)
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error Fetching AI Metrics!", errorMessage)
    }
  }

  // Fetch metrics on component mount and when filters change
  useEffect(() => {
    fetchMetrics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Renders general usage metrics view
   */
  const renderGeneralMetrics = (data: GeneralMetricsResponse) => {
    const { usage, system } = data
    const { total, stats, byUser, byUnit } = usage

    // Prepare user breakdown table data
    const userTableData = Object.entries(byUser).map(([username, metrics]) => ({
      key: username,
      user: username,
      conversionCalls: metrics.conversionCalls,
      geminiCalls: metrics.geminiCalls,
      validationCalls: metrics.validationCalls,
      assistantTurnCalls: metrics.assistantTurnCalls ?? 0,
      totalTokens: metrics.tokens.total.toLocaleString(),
      totalCost: `$${metrics.costs.total.toFixed(4)}`,
    }))

    // Prepare unit breakdown table data
    const unitTableData = Object.entries(byUnit).map(([unitName, metrics]) => ({
      key: unitName,
      unit: unitName,
      conversionCalls: metrics.conversionCalls,
      geminiCalls: metrics.geminiCalls,
      validationCalls: metrics.validationCalls,
      assistantTurnCalls: metrics.assistantTurnCalls ?? 0,
      totalTokens: metrics.tokens.total.toLocaleString(),
      totalCost: `$${metrics.costs.total.toFixed(4)}`,
    }))

    const userColumns = [
      { title: "User", dataIndex: "user", key: "user" },
      {
        title: "Conversions",
        dataIndex: "conversionCalls",
        key: "conversionCalls",
      },
      { title: "Gemini Calls", dataIndex: "geminiCalls", key: "geminiCalls" },
      {
        title: "Validations",
        dataIndex: "validationCalls",
        key: "validationCalls",
      },
      {
        title: "Assistant Turns",
        dataIndex: "assistantTurnCalls",
        key: "assistantTurnCalls",
      },
      { title: "Total Tokens", dataIndex: "totalTokens", key: "totalTokens" },
      { title: "Total Cost", dataIndex: "totalCost", key: "totalCost" },
    ]

    const unitColumns = [
      { title: "Unit", dataIndex: "unit", key: "unit" },
      {
        title: "Conversions",
        dataIndex: "conversionCalls",
        key: "conversionCalls",
      },
      { title: "Gemini Calls", dataIndex: "geminiCalls", key: "geminiCalls" },
      {
        title: "Validations",
        dataIndex: "validationCalls",
        key: "validationCalls",
      },
      {
        title: "Assistant Turns",
        dataIndex: "assistantTurnCalls",
        key: "assistantTurnCalls",
      },
      { title: "Total Tokens", dataIndex: "totalTokens", key: "totalTokens" },
      { title: "Total Cost", dataIndex: "totalCost", key: "totalCost" },
    ]

    return (
      <div>
        {/* Summary Statistics */}
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Total Conversions"
                value={total.conversionCalls}
                prefix={<ApiOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Total Gemini Calls"
                value={total.geminiCalls}
                prefix={<ThunderboltOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Total Tokens"
                value={total.tokens.total}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Total Cost"
                value={total.costs.total}
                prefix={<DollarOutlined />}
                precision={4}
              />
            </Card>
          </Col>
        </Row>

        {/* Additional Stats */}
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Assistant Turns"
                value={total.assistantTurnCalls ?? 0}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Unique Users"
                value={stats.uniqueUsers}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic title="Unique Units" value={stats.uniqueUnits} />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="shadow-sm">
              <Statistic
                title="Avg Gemini Calls/Conversion"
                value={stats.averageGeminiCallsPerConversion}
              />
            </Card>
          </Col>
        </Row>

        {/* Token Breakdown */}
        <Card title="Token Breakdown" className="mb-4 shadow-sm">
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Input Tokens"
                value={total.tokens.input.toLocaleString()}
              />
              <div className="text-sm text-gray-500 mt-1">
                Cost: ${total.costs.input.toFixed(4)}
              </div>
            </Col>
            <Col span={8}>
              <Statistic
                title="Output Tokens"
                value={total.tokens.output.toLocaleString()}
              />
              <div className="text-sm text-gray-500 mt-1">
                Cost: ${total.costs.output.toFixed(4)}
              </div>
            </Col>
            <Col span={8}>
              <Statistic
                title="Cached Tokens"
                value={total.tokens.cached.toLocaleString()}
              />
              <div className="text-sm text-gray-500 mt-1">
                Cost: ${total.costs.cached.toFixed(4)}
              </div>
            </Col>
          </Row>
        </Card>

        {/* System Metrics */}
        <Card title="System Metrics" className="mb-4 shadow-sm">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic title="Request Count" value={system.requestCount} />
            </Col>
            <Col span={6}>
              <Statistic title="Error Count" value={system.errorCount} />
            </Col>
            <Col span={6}>
              <Statistic
                title="Avg Response Time"
                value={system.averageResponseTime}
                suffix="ms"
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Error Rate"
                value={system.errorRate}
                suffix="%"
              />
            </Col>
          </Row>
        </Card>

        {/* User Breakdown Table */}
        {!selectedUser && (
          <Card title="Usage by User" className="mb-4 shadow-sm">
            <Table
              dataSource={userTableData}
              columns={userColumns}
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        )}

        {/* Unit Breakdown Table */}
        {!selectedUnit && (
          <Card title="Usage by Unit" className="shadow-sm">
            <Table
              dataSource={unitTableData}
              columns={unitColumns}
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        )}
      </div>
    )
  }

  /**
   * Renders top users view
   */
  const renderTopUsers = (data: TopUsersResponse) => {
    const columns = [
      {
        title: "Rank",
        key: "rank",
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      { title: "User", dataIndex: "user", key: "user" },
      {
        title: "Conversion Calls",
        dataIndex: "conversionCalls",
        key: "conversionCalls",
      },
      { title: "Gemini Calls", dataIndex: "geminiCalls", key: "geminiCalls" },
      { title: "Total Calls", dataIndex: "totalCalls", key: "totalCalls" },
    ]

    return (
      <Card title={`Top ${data.data.length} Users`} className="shadow-sm">
        <Table
          dataSource={data.data.map((user, index) => ({
            ...user,
            key: index,
          }))}
          columns={columns}
          pagination={false}
          size="small"
        />
      </Card>
    )
  }

  /**
   * Renders top units view
   */
  const renderTopUnits = (data: TopUnitsResponse) => {
    const columns = [
      {
        title: "Rank",
        key: "rank",
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      { title: "Unit", dataIndex: "unit", key: "unit" },
      {
        title: "Conversion Calls",
        dataIndex: "conversionCalls",
        key: "conversionCalls",
      },
      { title: "Gemini Calls", dataIndex: "geminiCalls", key: "geminiCalls" },
      { title: "Total Calls", dataIndex: "totalCalls", key: "totalCalls" },
    ]

    return (
      <Card title={`Top ${data.data.length} Units`} className="shadow-sm">
        <Table
          dataSource={data.data.map((unit, index) => ({
            ...unit,
            key: index,
          }))}
          columns={columns}
          pagination={false}
          size="small"
        />
      </Card>
    )
  }

  /**
   * Renders the appropriate metrics view based on response type
   */
  const renderMetrics = () => {
    if (!metricsData) {
      return (
        <Card className="shadow-sm">
          <div className="text-center text-gray-500">
            Click "Fetch Metrics" to load AI usage data
          </div>
        </Card>
      )
    }

    if ("usage" in metricsData) {
      return renderGeneralMetrics(metricsData as GeneralMetricsResponse)
    } else if (metricsData.type === "top-users") {
      return renderTopUsers(metricsData as TopUsersResponse)
    } else if (metricsData.type === "top-units") {
      return renderTopUnits(metricsData as TopUnitsResponse)
    }

    return null
  }

  return (
    <div className="flex flex-col p-5 bg-gray-50">
      <Card title="AI Usage Metrics" className="mb-4 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          {/* View Type Selector */}
          <div>
            <label className="block mb-2 font-semibold">View Type:</label>
            <Select
              value={viewType}
              onChange={(value) => {
                setViewType(value)
                setSelectedUser(undefined)
                setSelectedUnit(undefined)
              }}
              style={{ width: 180 }}
            >
              <Option value="general">General Metrics</Option>
              <Option value="top-users">Top Users</Option>
              <Option value="top-units">Top Units</Option>
            </Select>
          </div>

          {/* Period Selector */}
          <div>
            <label className="block mb-2 font-semibold">Period:</label>
            <Select
              value={period}
              onChange={(value) => setPeriod(value)}
              style={{ width: 180 }}
            >
              <Option value="startup">Since Server Startup</Option>
              <Option value="today">Today</Option>
              <Option value="all-time">All Time</Option>
              <Option value="custom">Custom Date</Option>
            </Select>
          </div>

          {/* Custom Date Picker */}
          {period === "custom" && (
            <div>
              <label className="block mb-2 font-semibold">Date:</label>
              <DatePicker
                value={customDate}
                onChange={(date) => setCustomDate(date)}
                format="YYYY-MM-DD"
                style={{ width: 180 }}
              />
            </div>
          )}

          {/* Filters for General View */}
          {viewType === "general" && (
            <>
              <div>
                <label className="block mb-2 font-semibold">
                  Filter by User:
                </label>
                <Input
                  placeholder="Enter username"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value || undefined)}
                  style={{ width: 180 }}
                  allowClear
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold">
                  Filter by Unit:
                </label>
                <Select
                  placeholder="Select unit"
                  value={selectedUnit}
                  onChange={(value) => setSelectedUnit(value)}
                  style={{ width: 180 }}
                  allowClear
                >
                  {units.map((unit) => (
                    <Option key={unit} value={unit}>
                      {unit}
                    </Option>
                  ))}
                </Select>
              </div>
            </>
          )}

          {/* Limit for Top Views */}
          {(viewType === "top-users" || viewType === "top-units") && (
            <div>
              <label className="block mb-2 font-semibold">Limit:</label>
              <Select
                value={limit}
                onChange={(value) => setLimit(value)}
                style={{ width: 180 }}
              >
                <Option value={5}>Top 5</Option>
                <Option value={10}>Top 10</Option>
                <Option value={25}>Top 25</Option>
                <Option value={50}>Top 50</Option>
              </Select>
            </div>
          )}

          {/* Fetch Button */}
          <DataQButton
            type="primary"
            onClick={fetchMetrics}
            isLoading={isLoadingAiMetrics}
          >
            Fetch Metrics
          </DataQButton>
        </div>
      </Card>

      {/* Metrics Display */}
      {isLoadingAiMetrics ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      ) : (
        renderMetrics()
      )}
    </div>
  )
}

export default AiMetricsDisplay
